import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import {
  HandCoins,
  Timer,
  Percent,
  Wallet,
  Save,
  Loader2,
  Sparkles,
  RefreshCw,
  BellRing,
} from "lucide-react";

/**
 * Platform Admin → Referral Settings
 * Configurable knobs for the entire commission engine. Persisted to
 * db.platform_settings.id='referral' so future business changes never
 * require a code deploy.
 */
export default function AdminReferralSettings() {
  const [data, setData] = useState(null);
  const [choices, setChoices] = useState({ eligible_plans: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/referral");
      setData(r.data.data);
      setChoices(r.data.choices || { eligible_plans: [] });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/referral", data);
      toast.success("Referral settings saved — applies to future signups & payments only.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const togglePlan = (plan) => {
    const set = new Set(data.eligible_plans || []);
    if (set.has(plan)) set.delete(plan);
    else set.add(plan);
    setData({ ...data, eligible_plans: Array.from(set) });
  };

  if (loading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6" data-testid="admin-referral-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2.5 text-white">
            <HandCoins className="w-7 h-7 text-emerald-400" /> Referral Program Settings
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            Live knobs for trial length, commission rates, and payout rules. Applies to <b className="text-emerald-400">new signups &amp; future payments</b> only — existing shops and commission history stay untouched.
          </p>
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="border-slate-700 text-slate-200 hover:bg-slate-800"
          data-testid="refset-refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Flat-price model (Feb 2026 FINAL) */}
      <section className="rounded-md border border-slate-800 bg-[#0F131C] p-6" data-testid="refset-flatprice-section">
        <div className="flex items-center gap-2 mb-4">
          <HandCoins className="w-5 h-5 text-emerald-400" />
          <h2 className="font-display font-semibold text-lg text-white">Subscription &amp; Revenue Split</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Feb 30 2026 FINAL — Monthly + Yearly plans with tiered referral rewards. Applied to every new paid subscription approval.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="plan_price" className="text-slate-300">Monthly Price (PKR)</Label>
            <Input
              id="plan_price" type="number" min={0} step={50}
              value={data.plan_price ?? 1500}
              onChange={(e) => setData({ ...data, plan_price: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-plan-price-input"
            />
            <div className="text-[11px] text-slate-500">Monthly subscription fee (default PKR 1,500).</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan_price_yearly" className="text-slate-300">Yearly Price (PKR)</Label>
            <Input
              id="plan_price_yearly" type="number" min={0} step={100}
              value={data.plan_price_yearly ?? 15000}
              onChange={(e) => setData({ ...data, plan_price_yearly: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-plan-price-yearly-input"
            />
            <div className="text-[11px] text-slate-500">Yearly subscription fee (default PKR 15,000).</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref_reward" className="text-slate-300">Monthly Referral Reward (PKR)</Label>
            <Input
              id="ref_reward" type="number" min={0} step={50}
              value={data.referral_reward_fixed ?? 500}
              onChange={(e) => setData({ ...data, referral_reward_fixed: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-ref-reward-input"
            />
            <div className="text-[11px] text-slate-500">Fixed reward on monthly subscription (default PKR 500).</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ref_reward_yearly" className="text-slate-300">Yearly Referral Reward (PKR)</Label>
            <Input
              id="ref_reward_yearly" type="number" min={0} step={100}
              value={data.referral_reward_yearly ?? 5000}
              onChange={(e) => setData({ ...data, referral_reward_yearly: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-ref-reward-yearly-input"
            />
            <div className="text-[11px] text-slate-500">Fixed reward on yearly subscription (default PKR 5,000).</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sa_pct" className="text-slate-300">Sub Admin Share %</Label>
            <Input
              id="sa_pct" type="number" min={0} max={100} step={1}
              value={data.sub_admin_share_pct ?? 40}
              onChange={(e) => setData({ ...data, sub_admin_share_pct: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-sub-admin-pct-input"
            />
            <div className="text-[11px] text-slate-500">Of net revenue after referral. Must sum to 100% with Super Admin.</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sup_pct" className="text-slate-300">Super Admin Share %</Label>
            <Input
              id="sup_pct" type="number" min={0} max={100} step={1}
              value={data.super_admin_share_pct ?? 60}
              onChange={(e) => setData({ ...data, super_admin_share_pct: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-super-admin-pct-input"
            />
            <div className="text-[11px] text-slate-500">Residual of net revenue. Must sum to 100% with Sub Admin.</div>
          </div>
        </div>
        <div className="mt-4 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3 text-[12px] text-emerald-200 space-y-1.5">
          <div className="font-bold text-emerald-300 uppercase tracking-wider text-[10px]">Monthly (PKR {(data.plan_price ?? 1500).toLocaleString()})</div>
          <div>Without referral: Sub Admin = <b>PKR {Math.round((data.plan_price ?? 1500) * (data.sub_admin_share_pct ?? 40) / 100).toLocaleString()}</b> · Super Admin = <b>PKR {Math.round((data.plan_price ?? 1500) * (data.super_admin_share_pct ?? 60) / 100).toLocaleString()}</b></div>
          <div>With referral: Referrer = <b>PKR {(data.referral_reward_fixed ?? 500).toLocaleString()}</b> · Sub Admin = <b>PKR {Math.round(((data.plan_price ?? 1500) - (data.referral_reward_fixed ?? 500)) * (data.sub_admin_share_pct ?? 40) / 100).toLocaleString()}</b> · Super Admin = <b>PKR {Math.round(((data.plan_price ?? 1500) - (data.referral_reward_fixed ?? 500)) * (data.super_admin_share_pct ?? 60) / 100).toLocaleString()}</b></div>
          <div className="font-bold text-emerald-300 uppercase tracking-wider text-[10px] pt-1.5 border-t border-emerald-500/10">Yearly (PKR {(data.plan_price_yearly ?? 15000).toLocaleString()})</div>
          <div>Without referral: Sub Admin = <b>PKR {Math.round((data.plan_price_yearly ?? 15000) * (data.sub_admin_share_pct ?? 40) / 100).toLocaleString()}</b> · Super Admin = <b>PKR {Math.round((data.plan_price_yearly ?? 15000) * (data.super_admin_share_pct ?? 60) / 100).toLocaleString()}</b></div>
          <div>With referral: Referrer = <b>PKR {(data.referral_reward_yearly ?? 5000).toLocaleString()}</b> · Sub Admin = <b>PKR {Math.round(((data.plan_price_yearly ?? 15000) - (data.referral_reward_yearly ?? 5000)) * (data.sub_admin_share_pct ?? 40) / 100).toLocaleString()}</b> · Super Admin = <b>PKR {Math.round(((data.plan_price_yearly ?? 15000) - (data.referral_reward_yearly ?? 5000)) * (data.super_admin_share_pct ?? 60) / 100).toLocaleString()}</b></div>
        </div>
      </section>

      {/* Trial */}
      <section className="rounded-md border border-slate-800 bg-[#0F131C] p-6" data-testid="refset-trial-section">
        <div className="flex items-center gap-2 mb-4">
          <Timer className="w-5 h-5 text-amber-400" />
          <h2 className="font-display font-semibold text-lg text-white">Free Trial</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="trial_days" className="text-slate-300">Free Trial Days</Label>
            <Input
              id="trial_days"
              type="number"
              min={0}
              max={90}
              value={data.trial_days}
              onChange={(e) => setData({ ...data, trial_days: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-trial-days-input"
            />
            <div className="text-[11px] text-slate-500">
              Currently: <b className="text-slate-300">{data.trial_days} days</b>. Set to <b>0</b> for no free trial (PKR {(data.plan_price ?? 1500).toLocaleString()} required upfront).
            </div>
          </div>
        </div>
      </section>

      {/* Legacy Commission rates (percentages) — retained for admin transparency but engine ignores them */}
      <section className="rounded-md border border-slate-800 bg-[#0F131C] p-6 opacity-70" data-testid="refset-rates-section">
        <div className="flex items-center gap-2 mb-4">
          <Percent className="w-5 h-5 text-slate-400" />
          <h2 className="font-display font-semibold text-lg text-slate-300">Legacy Percentage Rates <span className="text-[11px] font-normal text-slate-500">(display only — ignored by new engine)</span></h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="first_rate" className="text-slate-300">First Subscription Commission %</Label>
            <Input
              id="first_rate"
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={data.first_commission_rate}
              onChange={(e) => setData({ ...data, first_commission_rate: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-first-rate-input"
            />
            <div className="text-[11px] text-slate-500">Legacy field — new engine uses fixed reward above.</div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="renewal_rate" className="text-slate-300">Renewal Commission %</Label>
            <Input
              id="renewal_rate"
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={data.renewal_commission_rate}
              onChange={(e) => setData({ ...data, renewal_commission_rate: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-renewal-rate-input"
            />
            <div className="text-[11px] text-slate-500">Legacy field — new engine uses fixed reward above.</div>
          </div>
        </div>

        <div className="mt-5 flex items-start justify-between gap-3 border-t border-slate-800 pt-4">
          <div>
            <div className="text-sm font-medium text-white">Recurring Commission Enabled</div>
            <div className="text-[11px] text-slate-500">
              If OFF, only the first-subscription bonus is paid. All renewals earn 0 %.
            </div>
          </div>
          <Switch
            checked={!!data.recurring_enabled}
            onCheckedChange={(v) => setData({ ...data, recurring_enabled: !!v })}
            data-testid="refset-recurring-toggle"
          />
        </div>
      </section>

      {/* Wallet */}
      <section className="rounded-md border border-slate-800 bg-[#0F131C] p-6" data-testid="refset-wallet-section">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-5 h-5 text-sky-400" />
          <h2 className="font-display font-semibold text-lg text-white">Payouts</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="min_wd" className="text-slate-300">Minimum Withdrawal (PKR)</Label>
            <Input
              id="min_wd"
              type="number"
              min={0}
              step={100}
              value={data.min_withdrawal}
              onChange={(e) => setData({ ...data, min_withdrawal: Number(e.target.value) })}
              className="bg-slate-900 border-slate-700 text-white"
              data-testid="refset-min-withdrawal-input"
            />
            <div className="text-[11px] text-slate-500">Referral partners can only withdraw when their wallet ≥ this amount.</div>
          </div>
        </div>
      </section>

      {/* Trial Expiry Notifications */}
      <section className="rounded-md border border-slate-800 bg-[#0F131C] p-6" data-testid="refset-trial-notify-section">
        <div className="flex items-center gap-2 mb-4">
          <BellRing className="w-5 h-5 text-amber-400" />
          <h2 className="font-display font-semibold text-lg text-white">Trial Expiry Notifications</h2>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          When a shop&apos;s free trial is about to expire (or has expired) send in-app notifications to the assigned Sales staff and the referring shop owner. Toggles apply to future notifications only — anything already sent stays in the bell.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-400">Windows</div>
            {[
              { key: "notify_2d_before",  label: "2 days before expiry" },
              { key: "notify_1d_before",  label: "1 day before expiry" },
              { key: "notify_expiry_day", label: "On expiry day" },
              { key: "notify_3d_after",   label: "3 days after expiry" },
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900 px-3 py-2">
                <div className="text-sm text-slate-200">{row.label}</div>
                <Switch
                  checked={!!(data.trial_notify || {})[row.key]}
                  onCheckedChange={(v) => setData({
                    ...data,
                    trial_notify: { ...(data.trial_notify || {}), [row.key]: !!v },
                  })}
                  data-testid={`refset-trial-${row.key.replace("notify_", "")}`}
                />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-400">Audiences</div>
            {[
              { key: "target_platform_staff",   label: "Platform Staff (assigned Sales)" },
              { key: "target_referral_partner", label: "Referral Partner (referring shop)" },
            ].map((row) => (
              <div key={row.key} className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900 px-3 py-2">
                <div className="text-sm text-slate-200">{row.label}</div>
                <Switch
                  checked={!!(data.trial_notify || {})[row.key]}
                  onCheckedChange={(v) => setData({
                    ...data,
                    trial_notify: { ...(data.trial_notify || {}), [row.key]: !!v },
                  })}
                  data-testid={`refset-trial-${row.key}`}
                />
              </div>
            ))}
            <div className="text-[11px] text-slate-500 mt-2">
              Turning both audiences off effectively disables the whole trial-expiry engine.
            </div>
          </div>
        </div>
      </section>

      {/* Eligible plans */}
      <section className="rounded-md border border-slate-800 bg-[#0F131C] p-6" data-testid="refset-plans-section">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-emerald-400" />
          <h2 className="font-display font-semibold text-lg text-white">Eligible Subscription Plans</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3">
          Only payments on the plans below will generate commission. The Trial plan is always excluded.
        </p>
        <div className="flex flex-wrap gap-2">
          {(choices.eligible_plans || []).map((plan) => {
            const active = (data.eligible_plans || []).includes(plan);
            return (
              <button
                key={plan}
                type="button"
                onClick={() => togglePlan(plan)}
                data-testid={`refset-plan-${plan}`}
                className={`px-4 py-2 rounded-full border font-medium text-sm capitalize transition ${
                  active
                    ? "bg-emerald-500/10 border-emerald-500 text-emerald-300"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
                }`}
              >
                {active ? "✓ " : ""}{plan}
              </button>
            );
          })}
        </div>
      </section>

      {/* Save */}
      <div className="sticky bottom-4 z-10">
        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 backdrop-blur px-4 py-3 flex items-center justify-between gap-3">
          <div className="text-xs text-emerald-200">
            Changes apply to new signups &amp; future subscription payments only. Existing referral codes, wallet balances, and commission history remain unchanged.
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="refset-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</> : <><Save className="w-4 h-4 mr-1.5" /> Save Settings</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
